import type { Action, Offer, Target, TargetOptions } from "../protocol";
import { sameTarget } from "./game-text";

// Casting or activating with targets, a target at a time (#308). The engine offers a way of using a
// card that names targets once, with its target options: each slot's legal targets, how many must be
// named and the most that may be. A modal spell has options per mode. Each pick narrows the options
// to those it still fits; `next` is what may be picked after it, and `done` the action the picks
// make whole, if they do, which may be taken now.
export type Aim = { source: number; options: Offer[]; picked: Target[]; next: Target[]; done: Action | null };

// The action an offer is for, naming no targets.
export const offerAction = (o: Offer): Action => o.values[0];

// Which way of using a card an action is: its kind, and which alternative cost or ability.
export function wayOf(a: Action): string {
  switch (a.kind) {
    case "CastAlternative": return `alt:${a.values[2]}`;
    case "ActivateAbility": return `ability:${a.values[1]}`;
    case "UnlockDoor": return `door:${a.values[1]}`;
    default: return a.kind;
  }
}

// A card's offers, one list per way of using it, in the order the engine offered them.
export function waysToUse(offers: Offer[]): Offer[][] {
  const ways = new Map<string, Offer[]>();
  for (const o of offers) {
    const key = wayOf(offerAction(o));
    const way = ways.get(key);
    if (way) way.push(o);
    else ways.set(key, [o]);
  }
  return [...ways.values()];
}

export const takesTargets = (way: Offer[]): boolean => way.some((o) => o.kind === "Targeted" && o.values[1].most > 0);

// What target i may be under the options: its slot's, or past them, any of "any number of"'s.
const candidatesAt = (o: TargetOptions, i: number): Target[] => (i < o.slots.length ? o.slots[i]! : o.any_number_of);

// The life naming `t` taxes the cast with (601.2f, Terror of the Peaks), once however often it's named.
const lifeTaxOf = (o: TargetOptions, t: Target): number => o.life_taxes.find((tax) => sameTarget(tax.target, t))?.life ?? 0;

function namedLifeTax(o: TargetOptions, picked: Target[]): number {
  return picked.reduce((sum, t, i) => (picked.slice(0, i).some((p) => sameTarget(p, t)) ? sum : sum + lifeTaxOf(o, t)), 0);
}

const manaValueOf = (o: TargetOptions, t: Target): number | undefined => o.one_per_mana_value.find((m) => sameTarget(m.target, t))?.mana_value;

const sharesAManaValue = (o: TargetOptions, picked: Target[], t: Target): boolean => {
  const mv = manaValueOf(o, t);
  return mv !== undefined && picked.some((p) => manaValueOf(o, p) === mv);
};

// "Another target …": slot i names none of the targets picked before it.
const repeatsForAnother = (o: TargetOptions, picked: Target[], i: number, t: Target): boolean =>
  o.another_slots.includes(i) && picked.slice(0, i).some((p) => sameTarget(p, t));

// Whether `picked` begins a list the options admit: each in its slot's targets, the optional ones,
// past the required, never one already picked, nor "another target …", their life taxes within
// what there's to spare, and no two sharing a mana value when the options ask for one per mana value.
function begins(o: TargetOptions, picked: Target[]): boolean {
  return picked.length <= o.most && namedLifeTax(o, picked) <= o.life_to_spare && picked.every((t, i) =>
    candidatesAt(o, i).some((c) => sameTarget(c, t)) && (i < o.least || !picked.slice(o.least, i).some((p) => sameTarget(p, t)))
      && !sharesAManaValue(o, picked.slice(0, i), t) && !repeatsForAnother(o, picked, i, t));
}

function withTargets(a: Action, targets: Target[]): Action {
  switch (a.kind) {
    case "CastSpell": return { kind: "CastSpell", values: [a.values[0], targets] };
    case "CastAlternative": return { kind: "CastAlternative", values: [a.values[0], targets, a.values[2]] };
    case "CastBackFace": return { kind: "CastBackFace", values: [a.values[0], targets] };
    case "ActivateAbility": return { kind: "ActivateAbility", values: [a.values[0], a.values[1], targets] };
    default: return a;
  }
}

// The aim at a card's way of using it with `picked` so far: what the options still fitting it allow
// next, and the action it makes whole.
export function aimAt(source: number, options: Offer[], picked: Target[]): Aim {
  const next: Target[] = [];
  let done: Action | null = null;
  for (const o of options) {
    if (o.kind === "Untargeted") {
      if (picked.length === 0) done ??= o.values[0];
      continue;
    }
    const [action, opts] = o.values;
    if (!begins(opts, picked)) continue;
    if (picked.length >= opts.least) done ??= withTargets(action, picked);
    if (picked.length >= opts.most) continue;
    const optional = picked.slice(opts.least);
    const spare = opts.life_to_spare - namedLifeTax(opts, picked);
    for (const c of candidatesAt(opts, picked.length)) {
      if (picked.length >= opts.least && optional.some((p) => sameTarget(p, c))) continue;
      if (!picked.some((p) => sameTarget(p, c)) && lifeTaxOf(opts, c) > spare) continue;
      if (sharesAManaValue(opts, picked, c)) continue;
      if (repeatsForAnother(opts, picked, picked.length, c)) continue;
      if (!next.some((t) => sameTarget(t, c))) next.push(c);
    }
  }
  return { source, options, picked, next, done };
}

export const aimCandidates = (aim: Aim): Target[] => aim.next;

export const aimComplete = (aim: Aim): Action | null => aim.done;

export function aimedAt(aim: Aim, t: Target): Aim {
  return aimAt(aim.source, aim.options, [...aim.picked, t]);
}

// The aim without a target picked before, when the picks left still start a list the options admit.
export function aimedWithout(aim: Aim, t: Target): Aim | null {
  const left = aim.picked.filter((p) => !sameTarget(p, t));
  if (left.length === aim.picked.length) return null;
  const without = aimAt(aim.source, aim.options, left);
  return without.next.length > 0 || without.done ? without : null;
}

export const isPicked = (aim: Aim, t: Target): boolean => aim.picked.some((p) => sameTarget(p, t));

// The candidates as keys, worked out once per list: a board of a thousand permanents asks of each.
const candidateKeys = new WeakMap<Target[], Set<string>>();
const targetKey = (t: Target): string => `${t.kind}:${t.values[0]}`;

export function isCandidate(candidates: Target[], t: Target): boolean {
  let keys = candidateKeys.get(candidates);
  if (!keys) {
    keys = new Set(candidates.map(targetKey));
    candidateKeys.set(candidates, keys);
  }
  return keys.has(targetKey(t));
}

export const objectTarget = (oid: number): Target => ({ kind: "TargetObject", values: [oid] });
export const playerTarget = (pid: string): Target => ({ kind: "TargetPlayer", values: [pid] });
